import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Pause, Pencil, Play, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { cn, toaster, useDebouncedValue, useMediaQuery } from '@/shared/libs';
import {
  Button,
  CountryCodes,
  Dialog,
  DialogFooter,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  ListEmpty,
  ListToolbar,
  type ListToolbarViewMode,
  LoaderOverlay,
  PageHeader,
  Pagination,
  type PaginationPageSize,
  TableController,
  type TableHeader,
} from '@/shared/ui';
import {
  PROVIDER_STATUS_FILTER_OPTIONS,
  PROVIDER_STATUS_I18N_KEY,
  ProviderName,
  useDeleteProviderMutation,
  useProviders,
  useRestoreProviderMutation,
} from '@entities/Provider';
import type { AvailabilityStatus, Provider } from '@psp-aggregator/sdk';

import { PauseProviderModal } from '../PauseProviderModal';

import { ProviderCard } from '../ProviderCard/ProviderCard';
import { ProviderDetailsModal } from '../ProviderDetailsModal';
import {
  EMPTY_PROVIDER_FILTERS,
  ProviderFilters,
  type ProviderFilterValues,
} from '../ProviderFilters';
import { ProviderFormModal } from '../ProviderFormModal';
import { ResumeProviderModal } from '../ResumeProviderModal';

export interface ProvidersListProps {
  onGoToDeals?: () => void;
  statusOptions?: readonly AvailabilityStatus[];
  title?: string;
}

export const ProvidersList: FC<ProvidersListProps> = ({
  onGoToDeals,
  statusOptions = PROVIDER_STATUS_FILTER_OPTIONS,
  title,
}) => {
  const { t } = useTranslation();
  const isNarrow = useMediaQuery('(max-width: 767px)');
  const [view, setView] = useState<ListToolbarViewMode>('cards');
  const effectiveView: ListToolbarViewMode = isNarrow ? 'cards' : view;
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ProviderFilterValues>(
    EMPTY_PROVIDER_FILTERS,
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PaginationPageSize>(20);
  const [createOpen, setCreateOpen] = useState(false);
  const [editProvider, setEditProvider] = useState<Provider | null>(null);
  const [detailProvider, setDetailProvider] = useState<Provider | null>(null);
  const [pauseProvider, setPauseProvider] = useState<Provider | null>(null);
  const [resumeProvider, setResumeProvider] = useState<Provider | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Provider | null>(null);
  const deleteProvider = useDeleteProviderMutation();
  const restoreProvider = useRestoreProviderMutation();
  const debouncedSearch = useDebouncedValue(search.trim());

  const filled = (values: string[]): string[] | undefined =>
    values.length > 0 ? values : undefined;

  const providersQuery = useProviders({
    country: filled(filters.countries),
    currency: filled(filters.currencies),
    industry: filled(filters.industries),
    integrationMethod: filled(filters.integrations),
    isDeleted: showDeleted,
    limit: pageSize,
    page,
    paymentMethod: filled(filters.paymentMethods),
    search: debouncedSearch || undefined,
    status: filters.status || undefined,
  });

  const providers = providersQuery.data?.items ?? [];
  const total = providersQuery.data?.total ?? 0;
  const hasActiveFilters =
    search.trim() !== '' ||
    filters.status !== '' ||
    filters.countries.length > 0 ||
    filters.currencies.length > 0 ||
    filters.integrations.length > 0 ||
    filters.paymentMethods.length > 0 ||
    filters.industries.length > 0;
  const isListEmpty = !providersQuery.isLoading && total === 0;

  const clearFilters = (): void => {
    setSearch('');
    setFilters(EMPTY_PROVIDER_FILTERS);
    setPage(1);
  };

  const handleToggleStatus = (provider: Provider): void => {
    if ((provider.status ?? 'active') === 'paused') {
      setResumeProvider(provider);

      return;
    }

    setPauseProvider(provider);
  };

  const headers: TableHeader[] = [
    {
      key: true,
      title: t('pages.admin.providers.columns.name'),
      value: 'name',
    },
    {
      title: t('pages.admin.providers.columns.countries'),
      value: 'countries',
    },
    {
      title: t('pages.admin.providers.columns.currencies'),
      value: 'currencies',
    },
    {
      title: t('pages.admin.providers.columns.industries'),
      value: 'industries',
    },
    {
      title: t('pages.admin.providers.columns.offers'),
      value: 'offers',
    },
    {
      title: t('pages.admin.providers.columns.status'),
      value: 'status',
    },
    {
      title: '',
      value: 'actions',
    },
  ];

  const rows = providers.map((provider) => {
    const status = provider.status ?? 'active';
    const isActive = status === 'active';

    return {
      actions: (
        <div
          className="inline-flex items-center gap-1"
          onClick={(event) => event.stopPropagation()}>
          <button
            aria-label={t('buttons.edit')}
            className="cursor-pointer rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-brand/10 hover:text-brand"
            type="button"
            onClick={() => setEditProvider(provider)}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            aria-label={isActive ? t('buttons.pause') : t('buttons.resume')}
            className={cn(
              'cursor-pointer rounded-lg p-1.5 transition-colors',
              isActive
                ? 'text-amber-600 hover:bg-amber-500/10 dark:text-amber-400'
                : 'text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400',
            )}
            type="button"
            onClick={() => handleToggleStatus(provider)}>
            {isActive ? (
              <Pause className="h-3.5 w-3.5" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
          </button>
          {!showDeleted && (
            <button
              aria-label={t('buttons.delete')}
              className="cursor-pointer rounded-lg p-1.5 text-rose-600 transition-colors hover:bg-rose-500/10 dark:text-rose-400"
              type="button"
              onClick={() => setDeleteTarget(provider)}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {showDeleted && (
            <button
              aria-label={t('buttons.restore')}
              className="cursor-pointer rounded-lg p-1.5 text-emerald-600 transition-colors hover:bg-emerald-500/10"
              type="button"
              onClick={() => {
                void restoreProvider.mutateAsync(provider.id);
              }}>
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
      countries: <CountryCodes codes={provider.countries ?? []} />,
      currencies: (provider.currencies ?? []).slice(0, 5).join(', ') || '—',
      id: provider.id,
      industries: (
        <div className="flex flex-wrap gap-1">
          {(provider.industries ?? []).slice(0, 3).map((industry) => (
            <span
              key={industry}
              className="rounded-md bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-medium text-violet-600 dark:text-violet-300">
              {industry}
            </span>
          ))}
        </div>
      ),
      name: <ProviderName alias={provider.alias} name={provider.real_name} />,
      offers: provider.offers_count ?? 0,
      status: (
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[10px]',
            isActive
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
              : 'bg-muted text-muted-foreground',
          )}>
          {t(PROVIDER_STATUS_I18N_KEY[status])}
        </span>
      ),
    };
  });

  if (providersQuery.isLoading) {
    return <LoaderOverlay show />;
  }

  if (providersQuery.isError) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('errors.providers.loadFailed')}</EmptyTitle>
          <EmptyDescription>{t('errors.unknown')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div>
      {!!title && (
        <PageHeader
          action={
            <Button
              size="sm"
              type="button"
              variant="brand"
              onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('pages.admin.providers.create')}
            </Button>
          }
          className="mb-6"
          title={title}
        />
      )}

      <ListToolbar
        count={providers.length}
        search={{
          onChange: (value) => {
            setSearch(value);
            setPage(1);
          },
          placeholder: t('pages.admin.providers.searchPlaceholder'),
          value: search,
        }}
        total={total}
        view={isNarrow ? undefined : { onChange: setView, value: view }}
      />

      <ProviderFilters
        statusOptions={statusOptions}
        values={filters}
        onChange={(next) => {
          setFilters(next);
          setPage(1);
        }}
      />
      <div className="mb-4">
        <Button
          size="sm"
          type="button"
          variant={showDeleted ? 'brand' : 'outline'}
          onClick={() => {
            setShowDeleted((current) => !current);
            setPage(1);
          }}>
          {showDeleted
            ? t('pages.admin.providers.showActive')
            : t('pages.admin.providers.showTrash')}
        </Button>
      </div>

      {isListEmpty ? (
        <ListEmpty
          variant={hasActiveFilters ? 'filtered' : 'empty'}
          onAction={hasActiveFilters ? clearFilters : undefined}
        />
      ) : effectiveView === 'cards' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {providers.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              onDelete={showDeleted ? undefined : setDeleteTarget}
              onEdit={setEditProvider}
              onOpen={setDetailProvider}
              onRestore={
                showDeleted
                  ? (item) => {
                      void restoreProvider
                        .mutateAsync(item.id)
                        .catch((error: unknown) => {
                          toaster.error({
                            title: isAxiosError(error)
                              ? (error.response?.data?.message ??
                                t('errors.providers.restoreFailed'))
                              : t('errors.providers.restoreFailed'),
                          });
                        });
                    }
                  : undefined
              }
              onToggleStatus={handleToggleStatus}
            />
          ))}
        </div>
      ) : (
        <TableController
          data={rows}
          emptyState={{
            filtered: hasActiveFilters,
            onClearFilters: clearFilters,
          }}
          headers={headers}
          onClickRow={(_event, row) => {
            const provider = providers.find((item) => item.id === row.id);

            if (provider) {
              setDetailProvider(provider);
            }
          }}
        />
      )}

      {!isListEmpty && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      )}

      {createOpen && <ProviderFormModal onClose={() => setCreateOpen(false)} />}

      {!!editProvider && (
        <ProviderFormModal
          provider={editProvider}
          onClose={() => setEditProvider(null)}
        />
      )}

      {!!detailProvider && (
        <ProviderDetailsModal
          provider={detailProvider}
          onClose={() => setDetailProvider(null)}
          onEdit={() => {
            setEditProvider(detailProvider);
            setDetailProvider(null);
          }}
          onToggleStatus={() => {
            handleToggleStatus(detailProvider);
            setDetailProvider(null);
          }}
        />
      )}

      {!!pauseProvider && (
        <PauseProviderModal
          provider={pauseProvider}
          onClose={() => setPauseProvider(null)}
          onReviewDeals={
            onGoToDeals
              ? () => {
                  setPauseProvider(null);
                  onGoToDeals();
                }
              : undefined
          }
        />
      )}

      {!!resumeProvider && (
        <ResumeProviderModal
          open
          provider={resumeProvider}
          onClose={() => setResumeProvider(null)}
        />
      )}
      {!!deleteTarget && (
        <Dialog
          title={t('pages.admin.providers.deleteTitle')}
          width={480}
          onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-muted-foreground">
            {t('pages.admin.providers.deleteDescription', {
              name: deleteTarget.alias || deleteTarget.real_name,
            })}
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}>
              {t('buttons.cancel')}
            </Button>
            <Button
              disabled={deleteProvider.isPending}
              type="button"
              variant="destructive"
              onClick={() => {
                void deleteProvider
                  .mutateAsync(deleteTarget.id)
                  .then(() => setDeleteTarget(null))
                  .catch((error: unknown) => {
                    toaster.error({
                      title: isAxiosError(error)
                        ? (error.response?.data?.message ??
                          t('errors.providers.deleteFailed'))
                        : t('errors.providers.deleteFailed'),
                    });
                  });
              }}>
              {t('buttons.delete')}
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </div>
  );
};

ProvidersList.displayName = 'ProvidersList';
